import React, {FC, useEffect, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import * as fa from "@fortawesome/free-solid-svg-icons";
import {HoverHint} from "../content/HoverHint";
import {copyToClipboard} from "../../util/copyToClipboard";
import {faEuroSign} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

const widthOptions = {
  'sm': 'w-sm',
  'md': 'w-md',
  'full': 'w-full',
}

interface StringLikeInputProps {
  type: "text"|"email"|"password"
  label: string
  value: string
  onChange: (newValue: string) => void
  width?: 'default'|'full'
  placeholder?: string
  focus?: boolean
  inline?: boolean
  readonly?: boolean
  inputRef?: React.RefObject<HTMLInputElement|HTMLTextAreaElement>
  onBlur?: (newValue: string) => void
  required?: boolean
  maxWidth?: keyof typeof widthOptions
}
interface TextAreaLikeInputProps {
  type: "textarea"
  label: string
  value: string
  onChange: (newValue: string) => void
  width?: 'default'|'full'
  grow?: boolean
  placeholder?: string
  focus?: boolean
  inline?: boolean
  readonly?: boolean
  inputRef?: React.RefObject<HTMLInputElement|HTMLTextAreaElement>
  required?: boolean
  maxWidth?: keyof typeof widthOptions
}
interface NumberLikeInputProps {
  type: "number"|"price"
  label: string
  value: number
  onChange: (newValue: number) => void
  width?: 'default'|'full'
  placeholder?: string
  focus?: boolean
  inline?: boolean
  readonly?: boolean
  inputRef?: React.RefObject<HTMLInputElement>
  step?: number
  min?: number
  max?: number
  required?: boolean
  maxWidth?: keyof typeof widthOptions
}
interface DateLikeInputProps{
  type: "date"
  label: string
  value: Date
  onChange: (newValue: Date) => void
  width?: 'default'|'full'
  placeholder?: string
  focus?: boolean
  inline?: boolean
  readonly?: boolean
  inputRef?: React.RefObject<HTMLInputElement>
  required?: boolean
  maxWidth?: keyof typeof widthOptions
}
interface TimeLikeInputProps{
  type: "time"
  label: string
  value: Date
  onChange: (newValue: Date) => void
  width?: 'default'|'full'
  placeholder?: string
  focus?: boolean
  inline?: boolean
  readonly?: boolean
  inputRef?: React.RefObject<HTMLInputElement>
  required?: boolean
  maxWidth?: keyof typeof widthOptions
}
type InputProps = StringLikeInputProps | TextAreaLikeInputProps | DateLikeInputProps | TimeLikeInputProps | NumberLikeInputProps
export const Input: FC<InputProps> = props => {
  return <label className={`flex text-sm font-medium text-brand-900 w-full ${props.inline ? 'flex-row whitespace-nowrap items-center' : 'flex-col'}`}>
    <div className={"flex flex-row"}>
      <span className={props.inline ? 'mr-2' : ''}>{props.label}{props.required !== false
        && props.required ? <span className="text-red-500 ml-0.5">*</span> : <></>}
      </span>
    </div>
    <InputControl {...props} />
  </label>
}

export const InputControl: FC<InputProps> = props => {
  if (props.type === "textarea") {
    return <textarea
        autoFocus={props.focus}
        className={`border-2 border-slate-200 outline-brand-700 ${props.readonly ? 'text-slate-500 cursor-not-allowed' : 'text-black'} rounded-xs text-base font-normal px-2 py-2 mt-1 w-full`}
        style={props.grow ? {
          minHeight: `min(70vh,${2 + (props.value.split("\n").length * 1.5)}em)`,
          whiteSpace: 'pre',
          overflowWrap: 'normal',
          overflowX: 'scroll',
        } : {
          minHeight: '8rem',
        }}
        placeholder={props.placeholder}
        value={props.value}
        readOnly={props.readonly}
        ref={props.inputRef as React.RefObject<HTMLTextAreaElement>}
        onChange={(e) => props.onChange(e.target.value)}
    />
  }
  if (props.type === "date") {
    return <input
        autoFocus={props.focus}
        type={"date"}
        className={`border-2 border-slate-200 outline-brand-700 ${props.readonly ? 'text-slate-500 cursor-not-allowed' : 'text-black'} rounded-xs text-base font-normal px-2 mt-1 h-10 w-full`}
        placeholder={props.placeholder}
        readOnly={props.readonly}
        ref={props.inputRef}
        onChange={e => props.onChange(new Date(e.target.value))} value={`${props.value.getFullYear()}-${String(props.value.getMonth()+1).padStart(2, '0')}-${String(props.value.getDate()).padStart(2, '0')}`}
    />
  }
  if (props.type === "number") {
    return <input
      autoFocus={props.focus}
      type={"number"}
      className={`border-2 border-slate-200 outline-brand-700 ${props.readonly ? 'text-slate-500 cursor-not-allowed' : 'text-black'} rounded-xs text-base font-normal px-2 mt-1 h-10 w-full`}
      placeholder={props.placeholder}
      readOnly={props.readonly}
      ref={props.inputRef}
      min={props.min}
      max={props.max}
      step={props.step}
      onChange={e => props.onChange(Number(e.target.value))} value={`${props.value}`}
    />
  }
  if (props.type === "price") {
    return <div className={'flex mt-1'}>
      <div className={'h-10 w-10 bg-slate-100 border-2 border-r-0 rounded-l border-slate-200 flex items-center justify-center'}>
        <FontAwesomeIcon icon={faEuroSign} className={''}/>
      </div>
      <input
        autoFocus={props.focus}
        type={"number"}
        className={`border-2 border-slate-200 outline-brand-700 ${props.readonly ? 'text-slate-500 cursor-not-allowed' : 'text-black'} rounded-r text-base font-normal px-2 h-10 w-full`}
        placeholder={props.placeholder}
        readOnly={props.readonly}
        step={0.01}

        ref={props.inputRef}
        onChange={e => props.onChange(Number(e.target.value))} value={`${props.value}`}
      />
    </div>
  }
  if (props.type === "time") {
    if (props.type === "time") {
      const valueDate = moment(props.value)
      return <TimeInput value={[valueDate.hours(),valueDate.minutes()]} onChange={([h,m]) => {
        const d = moment(props.value)
        d.set('hours', h)
        d.set('minutes', m)
        props.onChange(d.toDate());
      }} />
    }
  }

  if (props.type === "text" || props.type === "password" || props.type === "email") {
    return <input
      autoFocus={props.focus}
      type={props.type}
      className={`border-2 border-slate-200 outline-brand-700 ${props.readonly ? 'text-slate-500 cursor-not-allowed' : 'text-black'} rounded-xs text-base font-normal px-2 mt-1 h-10 w-full`}
      placeholder={props.placeholder}
      value={props.value}
      readOnly={props.readonly}
      ref={props.inputRef as React.RefObject<HTMLInputElement>}
      // @ts-ignore
      onChange={(e) => props.onChange(e.target.value)}
      onBlur={(e) => props.onBlur?.(e.target.value)}
    />
  }
}

export const CopyableInput: FC<Pick<StringLikeInputProps, "label"|"value">> = props => {
  const [justCopied, setJustCopied] = useState(false)
  const copy = async () => {
    await copyToClipboard(props.value)
    setJustCopied(true)
    setTimeout(() => setJustCopied(false), 1000)
  }
  return <label className={"flex flex-col text-sm font-medium text-brand-900 w-full max-w-md"}>
    {props.label}
    <div className={"border-2 border-slate-200 outline-brand-700 rounded-xs mt-1 relative"}>
      <input
        type={"text"}
        readOnly={true}
        className={"text-black text-base font-normal h-10 px-2 w-full"}
        value={props.value}
      />
      <button className={'absolute top-1 right-1 h-8 w-8 rounded-xs hover:bg-slate-100'} onClick={() => copy()}>
        <HoverHint hint={'Copy to clipboard'}>
          <div className={"h-full w-full flex items-center justify-center"}>
            <FontAwesomeIcon icon={justCopied ? fa.faCheck : fa.faCopy} bounce={justCopied} className={"text-brand-800"} />
          </div>
        </HoverHint>
      </button>
    </div>
  </label>
}

const TimeInput: FC<{value: [number,number], onChange: (time: [number,number]) => void}> = props => {
  const [input, setInput] = useState(`${props.value[0]}:${props.value[1].toString().padStart(2, '0')}`)
  useEffect(() => {
    setInput(`${props.value[0]}:${props.value[1].toString().padStart(2, '0')}`)
  }, [props.value]);
  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      save()
    }
  }
  const save = () => {
    const digits = input.split('').filter(c => /\d/.test(c)).join('')
    if (digits.length === 0) {
      setInput('0:00')
      props.onChange([0,0])
      return
    } else if (digits.length <= 2) {
      setInput(`0:${digits.padStart(2, '0')}`)
      props.onChange([0, Number(digits)])
      return
    } else {
      const hours = Number(digits.slice(0, digits.length-2))
      const minutes = Number(digits.slice(digits.length-2))
      setInput(`${hours}:${minutes.toString().padStart(2, '0')}`)
      props.onChange([hours, minutes])
    }
  }
  return <input type="text" placeholder={'00:00'} onKeyDown={handleOnKeyDown} onBlur={() => save()} value={input} onChange={(e) => setInput(e.target.value)} className={'border-2 border-slate-200 outline-slate-700 text-black rounded-xs text-base font-normal px-2 mt-1 h-10 w-full'} />
}